import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Globe } from 'lucide-react';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const emneParam = searchParams.get('emne');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Tagarbejde',
    message: '',
    website_url: '', // Honeypot antispam felt
  });

  // SEO Titel og Meta Description for kontaktsiden
  useEffect(() => {
    document.title = "Kontakt Toa Byg ApS | Få et Uforpligtende Tilbud";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Har du spørgsmål eller brug for et uforpligtende tilbud? Kontakt Toa Byg ApS i Ishøj. Vi rykker ud på hele Sjælland med rådgivning og godt håndværk.');
  }, []);

  // Opdater form-data baseret på query-parametre ved indlæsning
  useEffect(() => {
    if (emneParam) {
      let mappedProjectType = 'Andet';
      const lowerEmne = emneParam.toLowerCase();
      if (lowerEmne.includes('tag') || lowerEmne.includes('asbest')) {
        mappedProjectType = 'Tagrenovering';
      } else if (lowerEmne.includes('tømrer') || lowerEmne.includes('terrasse') || lowerEmne.includes('vindu')) {
        mappedProjectType = 'Tømrerarbejde';
      } else if (lowerEmne.includes('renovering')) {
        mappedProjectType = 'Renovering';
      } else if (lowerEmne.includes('entreprise')) {
        mappedProjectType = 'Entrepriseopgaver';
      }

      setFormData(prev => ({
        ...prev,
        projectType: mappedProjectType,
        message: `Hej Toa Byg,\n\nJeg vil gerne have et uforpligtende tilbud på jeres priseksempel: "${emneParam}".\n\n[Skriv gerne yderligere detaljer om dit projekt her...]`
      }));
    }
  }, [emneParam]);

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Hvis honeypot-feltet er udfyldt, afviser vi lydløst (eller lader som om det lykkedes)
    if (formData.website_url) {
      setTimeout(() => {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Tagarbejde',
          message: '',
          website_url: '',
        });
      }, 1000);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          projectType: formData.projectType,
          message: formData.message,
          website_url: formData.website_url,
        }),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Tagarbejde',
          message: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der opstod en fejl ved afsendelse af formularen.');
      }
    } catch (err) {
      console.error('Fejl ved indsendelse af kontaktformular:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Kunne ikke oprette forbindelse til serveren. Prøv igen senere.',
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header Banner */}
      <section className="bg-blue-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-tight max-w-4xl mx-auto">
            Klar til at komme videre med dit byggeprojekt
          </h1>
          <p className="mt-6 text-base sm:text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Hvis du leder efter en entreprenør på Sjælland, der tager dit projekt alvorligt og taler i et sprog, der er til at forstå, er Toa Byg ApS klar til at hjælpe. Kontakt os for en uforpligtende samtale om tagrenovering, tømrerarbejde, renovering eller nedtagning af asbesttag.
          </p>
          <div className="mt-8 flex justify-center">
            <a
              href="tel:+4540942420"
              className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-none transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-0.5 duration-200 uppercase tracking-wider"
            >
              <Phone className="h-6 w-6 animate-pulse text-blue-200" />
              Ring til os: +45 40 94 24 20
            </a>
          </div>
        </div>
      </section>

      {/* Kontakt Sektion */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Venstre: Kontaktoplysninger (4 kolonner) */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white rounded-none p-8 shadow-sm border border-slate-100">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 uppercase">Vores kontaktoplysninger</h2>
                
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-none text-blue-600 shrink-0">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase">Adresse</h4>
                      <p className="text-slate-600 text-sm mt-1">Baldersbækvej 24, 2635 Ishøj</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-none text-blue-600 shrink-0">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase">Telefon</h4>
                      <a href="tel:+4540942420" className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-1 block uppercase">
                        +45 40 94 24 20
                      </a>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-none text-blue-600 shrink-0">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase">E-mail</h4>
                      <a href="mailto:toabyg1@gmail.com" className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-1 block uppercase">
                        toabyg1@gmail.com
                      </a>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-none text-blue-600 shrink-0">
                      <Globe className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase">Geografisk dækning</h4>
                      <p className="text-slate-600 text-sm mt-1">Vi dækker hele Sjælland</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-none text-blue-600 shrink-0">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase">Åbningstider</h4>
                      <div className="text-slate-600 text-sm mt-1 space-y-0.5">
                        <p><span className="font-semibold">Mandag - Fredag:</span> 07:00 - 16:00</p>
                        <p><span className="font-semibold">Lørdag - Søndag:</span> Lukket</p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Visuelt kort (Ishøj base) */}
              <div className="bg-slate-200 rounded-none overflow-hidden h-64 shadow-sm border border-slate-300 relative">
                {/* Her simulerer vi et kort */}
                <div className="absolute inset-0 bg-blue-50 flex flex-col items-center justify-center p-6 text-center">
                  <MapPin className="h-10 w-10 text-blue-600 animate-bounce mb-2" />
                  <h4 className="font-bold text-slate-900 uppercase">Toa Byg ApS dækker hele Sjælland</h4>
                  <p className="text-xs text-slate-500 mt-1">Med base i Ishøj kører vi ud til opgaver på hele Sjælland.</p>
                </div>
              </div>
            </div>

            {/* Højre: Kontaktformular (7 kolonner) */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-none p-8 sm:p-10 shadow-sm border border-slate-100">
                <h3 className="text-2xl font-bold text-slate-900 mb-2 uppercase">Send os en besked</h3>
                <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                  Udfyld formularen nedenfor med dine kontaktoplysninger og en kort beskrivelse af dit projekt, så vender vi tilbage til dig med et uforpligtende tilbud inden for 24 timer.
                </p>

                {status.success ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-none flex items-start gap-3 animate-in fade-in duration-300">
                    <CheckCircle className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-emerald-900 uppercase">Tak for din henvendelse!</h4>
                      <p className="text-sm text-emerald-700 mt-1">
                        Vi har modtaget din besked og vil gennemgå den hurtigst muligt. Du kan forvente at høre fra os inden for 24 timer på hverdage.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* ANTISPAM HONEYPOT FIELD - ABSOLUTELY HIDDEN FROM HUMANS */}
                    <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }}>
                      <label htmlFor="website_url">Efterlad dette felt tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {status.error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-none flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                        <span className="text-sm font-medium">{status.error}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Navn */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                          Dit navn <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                          placeholder="F.eks. Anders Andersen"
                        />
                      </div>

                      {/* Telefon */}
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                          placeholder="F.eks. 12 34 56 78"
                        />
                      </div>
                    </div>

                    {/* E-mail */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                        E-mailadresse <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                        placeholder="F.eks. anders@mail.dk"
                      />
                    </div>

                    {/* Projekt Type */}
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Hvad drejer opgaven sig om?
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-slate-900 bg-white"
                      >
                        <option value="Tagarbejde">Tagarbejde</option>
                        <option value="Tagrenovering">Tagrenovering</option>
                        <option value="Renovering">Renovering</option>
                        <option value="Tømrerarbejde">Tømrerarbejde</option>
                        <option value="Entrepriseopgaver">Entrepriseopgaver</option>
                        <option value="Andet">Andet</option>
                      </select>
                    </div>

                    {/* Besked */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Beskrivelse af opgaven <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-none border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-slate-900 resize-none"
                        placeholder="Beskriv dit projekt så detaljeret som muligt, f.eks. tagets størrelse, materialepræferencer osv."
                      />
                    </div>

                    {/* Submit Knap */}
                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 transition-colors shadow-md cursor-pointer uppercase tracking-wider"
                    >
                      {status.submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Sender besked...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Send henvendelse
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
